import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ButtonGroupContext,
  ButtonGroupContextType,
} from '../../../Contexts/ButtonGroupContext';
import { QMultiRootEditorContext } from '../../QMultiRootEditor/Context';

type ButtonsContextWrapperProps = {
  children?: ReactNode[];
};

export const ButtonsContextWrapper = ({
  children,
}: ButtonsContextWrapperProps) => {
  const [isButtonGroupDisabled, setIsButtonGroupDisabled] = useState(false);
  const [hasPendingEditorActions, setHasPendingEditorActions] = useState(false);
  const { editor: docEditor, ccEditor } = useContext(QMultiRootEditorContext);

  useEffect(() => {
    const editors = [docEditor, ccEditor].filter(Boolean);

    if (editors.length === 0) {
      setHasPendingEditorActions(false);
      return;
    }

    const syncPendingActions = () => {
      setHasPendingEditorActions(
        editors.some((editor) => editor!.plugins.get('PendingActions').hasAny),
      );
    };

    const cleanups = editors.map((editor) => {
      const pendingActions = editor!.plugins.get('PendingActions');
      pendingActions.on('change:hasAny', syncPendingActions);
      return () => pendingActions.off('change:hasAny', syncPendingActions);
    });

    syncPendingActions();

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [docEditor, ccEditor]);

  const setDisabled = useCallback(
    (isLoading: boolean, setLoading?: (isLoading: boolean) => void) => {
      setIsButtonGroupDisabled(isLoading);
      if (setLoading) {
        setLoading(isLoading);
      }
    },
    [],
  );

  const buttonGroupContext: ButtonGroupContextType = useMemo(
    () => ({
      isDisabled: isButtonGroupDisabled || hasPendingEditorActions,
      setDisabled: setDisabled,
    }),
    [isButtonGroupDisabled, hasPendingEditorActions, setDisabled],
  );

  return (
    <ButtonGroupContext.Provider value={buttonGroupContext}>
      {children}
    </ButtonGroupContext.Provider>
  );
};
